import { arrayMove } from '@dnd-kit/sortable';

import type { TTablesSettingsActions } from './actions';
import { TablesSettingsActionTypes } from './actions';
export type IFBmanagerTableName = 'accounts' | 'ads' | 'adsets' | 'cabs' | 'campaigns' | 'users';

export type TablesSettingsState = Record<IFBmanagerTableName, ITableSettingsTableColumns>;

interface ITableSettingsTableColumns {
  activeColumns: IFBmanagerTableColumn[];
  hiddenColumns: IFBmanagerTableColumn[];
}
export interface IFBmanagerTableColumn {
  label: string;
  value: string;
  key: string;
}

const baseColumns: IFBmanagerTableColumn[] = [
  { label: 'Показы', value: 'impressions', key: 'impressions' },
  { label: 'Клики по ссылке', value: 'linkClicks', key: 'linkClicks' },
  { label: 'Результаты', value: 'results', key: 'results' },
  { label: 'Лиды CPA', value: 'cpa', key: 'cpa' },
  { label: 'CTR', value: 'ctr', key: 'ctr' },
  { label: 'CPC', value: 'cpc', key: 'cpc' },
  { label: 'CPM', value: 'cpm', key: 'cpm' },
  { label: 'Расход', value: 'spent', key: 'spent' },
];

const baseHiddenColumns: IFBmanagerTableColumn[] = [
  { label: 'CPC (All)', value: 'cpcAll', key: 'cpcAll' },
  { label: 'CR', value: 'cr', key: 'cr' },
  { label: 'CR (All)', value: 'crAll', key: 'crAll' },
  { label: 'CTR (All)', value: 'ctrAll', key: 'ctrAll' },
  { label: 'Клики (All)', value: 'clicksAll', key: 'clicksAll' },
  { label: 'Завершенные регистрации', value: 'completeRegistration', key: 'completeRegistration' },
  {
    label: 'Цена за завершенную регистрацию',
    value: 'completeRegistrationCost',
    key: 'completeRegistrationCost',
  },
  { label: 'Цена за покупку', value: 'purchaseCost', key: 'purchaseCost' },
  { label: 'Покупка', value: 'purchase', key: 'purchase' },
  { label: 'Реакции', value: 'postReaction', key: 'postReaction' },
  { label: 'Сохранения', value: 'postSave', key: 'postSave' },
];

export const initialState: TablesSettingsState = {
  accounts: {
    activeColumns: [...baseColumns],
    hiddenColumns: [...baseHiddenColumns],
  },
  ads: {
    activeColumns: [
      { label: 'Кампания', value: 'campaign', key: 'campaign' },
      { label: 'Адсет', value: 'adset', key: 'adset' },
      { label: 'Объявление', value: 'ads', key: 'ads' },
      ...baseColumns,
    ],
    hiddenColumns: [...baseHiddenColumns],
  },
  adsets: {
    activeColumns: [...baseColumns],
    hiddenColumns: [...baseHiddenColumns],
  },
  cabs: {
    activeColumns: [...baseColumns],
    hiddenColumns: [...baseHiddenColumns],
  },
  campaigns: {
    activeColumns: [...baseColumns],
    hiddenColumns: [...baseHiddenColumns],
  },
  users: {
    activeColumns: [...baseColumns],
    hiddenColumns: [...baseHiddenColumns],
  },
};

export const tablesSettingsReducer = (
  state = initialState,
  { payload, type }: TTablesSettingsActions
): TablesSettingsState => {
  switch (type) {
    case TablesSettingsActionTypes.ADD_HIDDEN_COLUMN:
      if (state[payload.tableName].hiddenColumns.includes(payload.column)) {
        return state;
      }
      return {
        ...state,
        [payload.tableName]: {
          activeColumns: [...state[payload.tableName].activeColumns],
          hiddenColumns: [...state[payload.tableName].hiddenColumns, payload.column],
        },
      };

    case TablesSettingsActionTypes.SET_HIDDEN_COLUMNS:
      return {
        ...state,
        [payload.tableName]: {
          activeColumns: [...state[payload.tableName].activeColumns],
          hiddenColumns: payload.columns,
        },
      };

    case TablesSettingsActionTypes.REMOVE_HIDDEN_COLUMN:
      const filteredHiddenColumns = state[payload.tableName].hiddenColumns.filter(
        (el) => el.value !== payload.column.value
      );
      return {
        ...state,
        [payload.tableName]: {
          activeColumns: [...state[payload.tableName].activeColumns, payload.column],
          hiddenColumns: filteredHiddenColumns,
        },
      };

    case TablesSettingsActionTypes.SET_ACTIVE_COLUMNS_ORDER:
      const activeIndex = state[payload.tableName].activeColumns.findIndex((i) => i.key === payload.activeId);
      const overIndex = state[payload.tableName].activeColumns.findIndex((i) => i.key === payload.overId);
      const result = arrayMove(state[payload.tableName].activeColumns, activeIndex, overIndex);
      return {
        ...state,
        [payload.tableName]: {
          activeColumns: result,
          hiddenColumns: [...state[payload.tableName].hiddenColumns],
        },
      };

    case TablesSettingsActionTypes.REMOVE_ACTIVE_COLUMN:
      const filteredActiveColumns = state[payload.tableName].activeColumns.filter(
        (el) => el.value !== payload.column.value
      );
      return {
        ...state,
        [payload.tableName]: {
          activeColumns: filteredActiveColumns,
          hiddenColumns: [...state[payload.tableName].hiddenColumns, payload.column],
        },
      };

    case TablesSettingsActionTypes.RESET_STATE:
      return {
        ...state,
        [payload.tableName]: initialState[payload.tableName],
      };

    default:
      return state;
  }
};
